import React, { useEffect, useState, useCallback, useMemo } from "react";
import { PortalService } from "services/portal";
import { getPortalSocket } from "services/portal/socket";
import {
  Download, Eye, RefreshCw, Play, FileText, AlertCircle, Search,
  ArrowUpRight, ArrowDownLeft, Calendar, X as XIcon,
} from "lucide-react";
import { PdfViewerModal } from "components/PdfViewerModal";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" };
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function fmtBytes(n) {
  if (!n) return "—";
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(1)} KB`;
}

function fmtCLP(n) {
  if (n === null || n === undefined || n === 0) return "—";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function PortalDtes() {
  const now = new Date();
  const currentYear = now.getFullYear();
  // Defaults: mes anterior (los DTEs del mes actual recién están llegando).
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? currentYear - 1 : currentYear;

  const [period, setPeriod] = useState({ month: defaultMonth, year: defaultYear });
  const [maxDocuments, setMaxDocuments] = useState(20);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [progress, setProgress] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [viewingDte, setViewingDte] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [directionFilter, setDirectionFilter] = useState("ALL"); // ALL | EMITIDAS | RECIBIDAS
  const [search, setSearch] = useState("");

  const handleDownload = async (dte) => {
    setDownloadingId(dte._id);
    try {
      const res = await PortalService.getDteDownloadUrl(dte._id);
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.filename || `${dte.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || "No se pudo descargar" });
    } finally {
      setDownloadingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PortalService.getDtes(period);
      setItems(res.dtes || []);
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [period.month, period.year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const socket = getPortalSocket();
    if (!socket) return;
    const onStart = (p) => setProgress({ stage: "start", ...p });
    const onProgress = (p) => setProgress((prev) => ({ ...(prev || {}), ...p }));
    const onDone = (p) => {
      setProgress({ stage: "done", ...p });
      load();
      setTimeout(() => setProgress(null), 5000);
    };
    const onError = (p) => {
      setProgress({ stage: "error", ...p });
      setTimeout(() => setProgress(null), 8000);
    };
    socket.on("dte:start", onStart);
    socket.on("dte:progress", onProgress);
    socket.on("dte:done", onDone);
    socket.on("dte:error", onError);
    return () => {
      socket.off("dte:start", onStart);
      socket.off("dte:progress", onProgress);
      socket.off("dte:done", onDone);
      socket.off("dte:error", onError);
    };
  }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    setFeedback(null);
    setProgress({ stage: "start" });
    try {
      await PortalService.triggerDteSync({ ...period, direction: "BOTH", maxDocuments: parseInt(maxDocuments) });
      setFeedback({ kind: "ok", msg: `Descarga iniciada. Verás el avance abajo.` });
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
      setProgress(null);
    } finally {
      setTriggering(false);
    }
  };

  // Filtro derivado (dirección + búsqueda).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (directionFilter !== "ALL" && d.direction !== directionFilter) return false;
      if (!q) return true;
      return (
        (d.folio || "").toLowerCase().includes(q) ||
        (d.code || "").toLowerCase().includes(q) ||
        (d.rutContraparte || "").toLowerCase().includes(q) ||
        (d.razonSocial || "").toLowerCase().includes(q)
      );
    });
  }, [items, directionFilter, search]);

  const totals = useMemo(() => {
    const emitidas = items.filter((d) => d.direction === "EMITIDAS");
    const recibidas = items.filter((d) => d.direction === "RECIBIDAS");
    const sum = (arr) => arr.reduce((s, d) => s + (d.montoTotal || 0), 0);
    return {
      total: items.length,
      emitidas: emitidas.length,
      recibidas: recibidas.length,
      totalEmitidas: sum(emitidas),
      totalRecibidas: sum(recibidas),
    };
  }, [items]);

  const yearOptions = useMemo(() => {
    return [currentYear, currentYear - 1, currentYear - 2];
  }, [currentYear]);

  const counterparty = (d) => {
    if (d.razonSocial || d.rutContraparte) {
      return { name: d.razonSocial || "(Sin nombre)", rut: d.rutContraparte || "" };
    }
    return { name: "—", rut: "" };
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
          Facturas (PDF)
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
          DTEs emitidos y recibidos de tu empresa. Visualízalos en el portal o descárgalos a tu equipo.
        </p>
      </div>

      {/* Selector de período + acción */}
      <div style={{ ...CARD, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Calendar size={16} color="#0353a4" />
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>Período</span>
        </div>

        {/* Año en pills */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", background: "var(--bg-hover)", borderRadius: 999, padding: 4, gap: 2 }}>
            {yearOptions.map((y) => (
              <button
                key={y}
                onClick={() => setPeriod((p) => ({ ...p, year: y }))}
                style={{
                  padding: "6px 14px", fontSize: 13, fontWeight: 600,
                  border: "none", borderRadius: 999, cursor: "pointer",
                  background: period.year === y ? "var(--bg-card)" : "transparent",
                  color: period.year === y ? "#0353a4" : "#64748b",
                  boxShadow: period.year === y ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        {/* Mes en pills cortos */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 6, marginBottom: 14 }}>
          {MONTHS_SHORT.map((m, i) => {
            const monthNum = i + 1;
            const active = period.month === monthNum;
            return (
              <button
                key={m}
                onClick={() => setPeriod((p) => ({ ...p, month: monthNum }))}
                style={{
                  padding: "8px 4px", fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${active ? "#0353a4" : "#e2e8f0"}`,
                  borderRadius: 8, cursor: "pointer",
                  background: active ? "#0353a4" : "#fff",
                  color: active ? "var(--bg-card)" : "#475569",
                }}
              >
                {m}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Máx. documentos:</span>
            <input
              type="number"
              min={1}
              max={500}
              value={maxDocuments}
              onChange={(e) => setMaxDocuments(e.target.value)}
              style={{
                padding: "6px 10px", border: "1.5px solid var(--border)", borderRadius: 6,
                fontSize: 13, width: 80, color: "var(--text-secondary)",
              }}
            />
          </div>
          <button
            onClick={handleTrigger}
            disabled={triggering}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 18px", fontSize: 13, fontWeight: 600,
              background: triggering ? "#94a3b8" : "#0353a4", color: "#fff",
              border: "none", borderRadius: 8,
              cursor: triggering ? "not-allowed" : "pointer",
              boxShadow: "0 1px 4px rgba(3,83,164,0.25)",
            }}
          >
            {triggering ? <RefreshCw size={14} className="spin" /> : <Play size={14} />}
            {triggering ? "Iniciando..." : `Descargar PDFs de ${MONTHS[period.month - 1]} ${period.year}`}
          </button>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
            ~12s por documento · se suben a Azure
          </span>
        </div>

        {feedback && (
          <div
            style={{
              marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
              background: feedback.kind === "ok" ? "#ecfdf5" : "#fef2f2",
              border: `1px solid ${feedback.kind === "ok" ? "#bbf7d0" : "#fecaca"}`,
              color: feedback.kind === "ok" ? "#065f46" : "#991b1b",
            }}
          >
            {feedback.msg}
          </div>
        )}

        {progress && (
          <div
            style={{
              marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
              background: progress.stage === "error" ? "#fef2f2" : progress.stage === "done" ? "#ecfdf5" : "#eff6ff",
              border: "1px solid",
              borderColor: progress.stage === "error" ? "#fecaca" : progress.stage === "done" ? "#bbf7d0" : "#bfdbfe",
              color: progress.stage === "error" ? "#991b1b" : progress.stage === "done" ? "#065f46" : "#0c4a6e",
            }}
          >
            {progress.stage === "auth" && "Autenticando en SII..."}
            {progress.stage === "warming" && "Calentando sesión..."}
            {progress.stage === "scanning" && `Escaneando ${progress.direction?.toLowerCase()}...`}
            {progress.stage === "downloaded" && `Descargando ${progress.direction}: ${progress.index}/${progress.total} (${progress.code})`}
            {progress.stage === "skipped" && `Ya descargado: ${progress.code}`}
            {progress.stage === "error" && `Error: ${progress.message}`}
            {progress.stage === "done" && progress.summary && (
              <span>
                ✓ Completado. Descargados: <b>{progress.summary.downloaded}</b>, ya existentes: <b>{progress.summary.skipped}</b>, errores: <b>{progress.summary.errors}</b>
              </span>
            )}
            {progress.stage === "start" && "Iniciando..."}
          </div>
        )}
      </div>

      {/* Resumen del período */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <SummaryCard
          icon={<FileText size={18} color="#0353a4" />}
          label="Total DTEs"
          value={totals.total}
          sub={`${MONTHS[period.month - 1]} ${period.year}`}
        />
        <SummaryCard
          icon={<ArrowUpRight size={18} color="#059669" />}
          label="Emitidas"
          value={totals.emitidas}
          sub={fmtCLP(totals.totalEmitidas)}
          color="#059669"
        />
        <SummaryCard
          icon={<ArrowDownLeft size={18} color="#2563eb" />}
          label="Recibidas"
          value={totals.recibidas}
          sub={fmtCLP(totals.totalRecibidas)}
          color="#2563eb"
        />
      </div>

      {/* Listado */}
      <div style={{ ...CARD }}>
        <div
          style={{
            padding: "14px 20px", borderBottom: "1px solid var(--border-light)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, flexWrap: "wrap",
          }}
        >
          {/* Tabs dirección */}
          <div style={{ display: "inline-flex", background: "var(--bg-hover)", borderRadius: 8, padding: 3, gap: 2 }}>
            {[
              { k: "ALL", l: `Todas (${totals.total})` },
              { k: "EMITIDAS", l: `Emitidas (${totals.emitidas})` },
              { k: "RECIBIDAS", l: `Recibidas (${totals.recibidas})` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setDirectionFilter(t.k)}
                style={{
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  border: "none", borderRadius: 6, cursor: "pointer",
                  background: directionFilter === t.k ? "var(--bg-card)" : "transparent",
                  color: directionFilter === t.k ? "#0353a4" : "#64748b",
                  boxShadow: directionFilter === t.k ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {t.l}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 220px", justifyContent: "flex-end" }}>
            {/* Búsqueda */}
            <div style={{ position: "relative", flex: "0 1 260px", minWidth: 180 }}>
              <Search size={14} color="#94a3b8" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input
                type="text"
                placeholder="Buscar folio, RUT o razón social..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%", padding: "7px 30px 7px 30px",
                  border: "1.5px solid var(--border)", borderRadius: 8,
                  fontSize: 13, color: "var(--text-secondary)", background: "var(--bg-card)",
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)",
                    display: "inline-flex", padding: 2,
                  }}
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
            <button
              onClick={load}
              disabled={loading}
              style={{
                background: "transparent", border: "none", color: "var(--accent)",
                cursor: "pointer", fontSize: 13, fontWeight: 500,
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <RefreshCw size={13} className={loading ? "spin" : ""} /> Refrescar
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Cargando...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
            <FileText size={36} color="#cbd5e1" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>
              No hay PDFs descargados en este período
            </div>
            <div style={{ fontSize: 12 }}>Lanza una descarga arriba para traer las facturas desde el SII.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            No hay resultados para tu filtro. <button onClick={() => { setSearch(""); setDirectionFilter("ALL"); }} style={{ background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontWeight: 600 }}>Limpiar filtros</button>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-elevated)" }}>
                  {[
                    { l: "Tipo", w: 100 },
                    { l: "Folio", w: 110 },
                    { l: "Contraparte", w: null },
                    { l: "Fecha", w: 100 },
                    { l: "Monto", w: 130, align: "right" },
                    { l: "Estado", w: 100 },
                    { l: "Acciones", w: 170, align: "right" },
                  ].map((h) => (
                    <th
                      key={h.l}
                      style={{
                        padding: "10px 12px", textAlign: h.align || "left",
                        fontSize: 11, fontWeight: 600, color: "var(--text-secondary)",
                        textTransform: "uppercase", borderBottom: "2px solid var(--border)",
                        width: h.w || undefined, letterSpacing: 0.3,
                      }}
                    >
                      {h.l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const cp = counterparty(d);
                  const emitida = d.direction === "EMITIDAS";
                  const downloadable = !!(d.file?.blobName || d.file?.driveId || d.file?.webViewLink);
                  return (
                    <tr key={d._id} style={{ transition: "background 0.1s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-elevated)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ padding: "12px", fontSize: 13, borderBottom: "1px solid var(--border-light)" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                          background: emitida ? "#ecfdf5" : "#eff6ff",
                          color: emitida ? "#059669" : "#2563eb",
                        }}>
                          {emitida ? <ArrowUpRight size={11} /> : <ArrowDownLeft size={11} />}
                          {emitida ? "Emitida" : "Recibida"}
                        </span>
                        {d.tipoDoc && (
                          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                            {d.tipoDoc}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "12px", fontSize: 13, color: "var(--text-primary)", borderBottom: "1px solid var(--border-light)", fontWeight: 600 }}>
                        {d.folio || <code style={{ fontSize: 11, color: "var(--text-secondary)" }}>{d.code}</code>}
                      </td>
                      <td style={{ padding: "12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        <div style={{ fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.3 }}>{cp.name}</div>
                        {cp.rut && (
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{cp.rut}</div>
                        )}
                      </td>
                      <td style={{ padding: "12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)", whiteSpace: "nowrap" }}>
                        {fmtDate(d.fechaEmision)}
                      </td>
                      <td style={{ padding: "12px", fontSize: 13, color: "var(--text-primary)", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {fmtCLP(d.montoTotal)}
                        {d.file?.sizeBytes ? (
                          <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 400, marginTop: 2 }}>
                            {fmtBytes(d.file.sizeBytes)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: "12px", fontSize: 13, borderBottom: "1px solid var(--border-light)" }}>
                        {d.status === "DOWNLOADED" ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--positive)", fontSize: 12, fontWeight: 600 }}>
                            ✓ OK
                          </span>
                        ) : d.status === "FAILED" ? (
                          <span title={d.errorMessage} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--negative)", fontSize: 12, fontWeight: 600 }}>
                            <AlertCircle size={12} /> Falló
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{d.status}</span>
                        )}
                      </td>
                      <td style={{ padding: "12px", textAlign: "right", borderBottom: "1px solid var(--border-light)" }}>
                        {downloadable ? (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <button
                              onClick={() => setViewingDte(d)}
                              title="Ver PDF"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "5px 10px", fontSize: 12, fontWeight: 600,
                                background: "var(--info-bg)", border: "1px solid #bfdbfe",
                                borderRadius: 6, cursor: "pointer", color: "var(--accent)",
                              }}
                            >
                              <Eye size={12} /> Ver
                            </button>
                            <button
                              onClick={() => handleDownload(d)}
                              disabled={downloadingId === d._id}
                              title="Descargar PDF"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "5px 10px", fontSize: 12, fontWeight: 600,
                                background: "#0353a4", border: "none",
                                borderRadius: 6, color: "#fff",
                                cursor: downloadingId === d._id ? "not-allowed" : "pointer",
                                opacity: downloadingId === d._id ? 0.6 : 1,
                              }}
                            >
                              {downloadingId === d._id ? (
                                <RefreshCw size={12} className="spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              {downloadingId === d._id ? "..." : "PDF"}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "var(--text-muted)" }} title="Archivo aún no disponible">
                            Sin archivo
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PdfViewerModal
        open={!!viewingDte}
        dte={viewingDte}
        onClose={() => setViewingDte(null)}
      />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {icon}
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#0f172a", lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6 }}>{sub}</div>
      )}
    </div>
  );
}
